import React, { useState, useEffect } from 'react';
import { Table, Form, Button, Spinner, Alert, Tabs, Tab } from 'react-bootstrap';
import { FaSave, FaUndo } from 'react-icons/fa';
import { dsoApi } from '../../services/api';
import './Commissions.css';

function DSOCommissions() {
  // States for commissions
  const [products, setProducts] = useState([]);
  const [editedCommissions, setEditedCommissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState('');
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    fetchGlobalCommissions();
  }, []);


  const fetchGlobalCommissions = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await dsoApi.getCommissions();
      console.log('Global commissions:', response.data);
      
      const productList = response.data.products || [];
      
      // Log to see the category structure in products
      console.log('Products with categories:', productList.map(p => ({
        id: p.product_id,
        name: p.name,
        category: p.category,
      })));
      
      setProducts(productList);
      
      // Initialize edited commissions
      const initialEdited = {};
      productList.forEach(product => {
        initialEdited[product.product_id] = {
          commission_percentage: product.commission_percentage,
          commission_amount: product.commission_amount
        };
      });
      setEditedCommissions(initialEdited);
      
    } catch (err) {
      console.error('Error fetching commissions:', err);
      setError('Failed to load commission data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };


  const handleCommissionChange = (productId, field, value) => {
    // Validate numeric input
    if (field === 'commission_percentage' || field === 'commission_amount') {
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) return;
    }
    
    setEditedCommissions(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [field]: value
      }
    }));
    // Clear save status messages on global edit
    setSaveSuccess('');
    setSaveError('');
  };

  const handleSaveGlobalCommissions = async () => {
    setSaveLoading(true);
    setSaveSuccess('');
    setSaveError('');
    
    try {
      // Prepare data for API
      const commissionData = Object.entries(editedCommissions).map(([productId, values]) => ({
        product_id: parseInt(productId, 10),
        commission_percentage: parseFloat(values.commission_percentage),
        commission_amount: parseFloat(values.commission_amount)
      }));
      
      console.log('Saving global commissions:', commissionData);
      
      const response = await dsoApi.updateGlobalCommissions(commissionData);
      console.log('Save response:', response.data);
      
      setSaveSuccess('Global commissions updated successfully!');
      
      // Clear success message after a few seconds
      setTimeout(() => {
        setSaveSuccess('');
      }, 3000);
      
      // Refresh data
      fetchGlobalCommissions();
      
    } catch (err) {
      console.error('Error saving commissions:', err);
      setSaveError(err.response?.data?.detail || 'Failed to update commissions. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };




  const handleReset = () => {
    // Reset global commissions
    fetchGlobalCommissions(); // Re-fetch or reset from an original state if implemented
    setSaveSuccess('');
    setSaveError('');
  };


  // Group products by category
  const getProductsByCategory = () => {
    const grouped = {};
    products.forEach(product => {
      // Get category from the product
      // It could be directly in product.category or in product.category.category
      let categoryName = 'Uncategorized';
      
      if (product.category) {
        if (typeof product.category === 'object' && product.category.category) {
          categoryName = product.category.category;
        } else if (typeof product.category === 'string') {
          categoryName = product.category;
        }
      }
      
      if (!grouped[categoryName]) {
        grouped[categoryName] = [];
      }
      grouped[categoryName].push(product);
    });
    return grouped;
  };
  
  // CSS classes for styling categories
  const categoryStyle = {
    marginBottom: '30px',
    borderRadius: '5px',
    overflow: 'hidden',
    border: '1px solid #dee2e6',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
  };
  
  const categoryTitleStyle = {
    padding: '12px 18px',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #dee2e6',
    marginBottom: '0',
    fontWeight: '600',
    fontSize: '16px',
    color: '#333'
  };
  
  const commissionsTableStyle = {
    margin: '0'
  };

  // This function is no longer needed as it has been replaced by renderCategoryContent
  // and the new tabbed layout in the return statement



  const renderCategoryContent = (category) => {
    if (loading) {
      return (
        <div className="dso-commissions-loading">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      );
    }
    
    if (error) {
      return <Alert variant="danger">{error}</Alert>;
    }

    const productsByCategory = getProductsByCategory();
    const products = productsByCategory[category] || [];
    
    return (
      <div className="dso-commissions-table-container">
        <Table responsive striped className="dso-commissions-table" style={commissionsTableStyle}>
          <thead>
            <tr>
              <th>Bank</th>
              <th>Product</th>
              {category === 'Credit Card' ? (
                <th>Commission Amount (₹)</th>
              ) : (
                <th>Commission (%)</th>
              )}
            </tr>
          </thead>
          <tbody>
            {products.map((product) => (
              <tr key={product.product_id}>
                <td>{product.bank || 'N/A'}</td>
                <td>{product.name}</td>
                {category === 'Credit Card' ? (
                  <td>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={editedCommissions[product.product_id]?.commission_amount || 0}
                      onChange={(e) => handleCommissionChange(product.product_id, 'commission_amount', e.target.value)}
                      className="dso-commission-input"
                    />
                  </td>
                ) : (
                  <td>
                    <Form.Control
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={editedCommissions[product.product_id]?.commission_percentage || 0}
                      onChange={(e) => handleCommissionChange(product.product_id, 'commission_percentage', e.target.value)}
                      className="dso-commission-input"
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  return (
    <div className="dso-commissions-container">
      <h3 className="dso-commissions-title">Agent Commission Settings</h3>
      
      <div className="dso-commissions-actions">
        <Button 
          variant="primary" 
          onClick={handleSaveGlobalCommissions}
          disabled={saveLoading}
        >
          {saveLoading ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Saving...
            </>
          ) : (
            <>
              <FaSave className="me-2" /> Save Commissions
            </>
          )}
        </Button>
        <Button 
          variant="outline-secondary" 
          onClick={handleReset}
          disabled={saveLoading}
        >
          <FaUndo className="me-2" /> Reset
        </Button>
      </div>
      
      {saveSuccess && <Alert variant="success">{saveSuccess}</Alert>}
      {saveError && <Alert variant="danger">{saveError}</Alert>}
      
      <Tabs defaultActiveKey="Credit Card" id="commission-categories-tabs" className="mb-4">
        {Object.keys(getProductsByCategory()).sort().map((category) => (
          <Tab eventKey={category} title={category} key={category}>
            {renderCategoryContent(category)}
          </Tab>
        ))}
      </Tabs>
    </div>
  );
}

export default DSOCommissions;
